
import { fetchEventsAsNestedStructuredData } from '@/api/Event'
import { EventDetails, NestedEventDataWithSessions } from '@/api/Helpers'
import { Session } from '@/api/Sessions'
import MembersBanner from '@/components/elements/MembersBanner.vue'
import EventListingItem from '@/components/events/EventListingItem.vue'
import CategoryFilters from '@/components/filters/CategoryFilters.vue'
import DateFilters from '@/components/filters/DateFilters.vue'
import TimeOfDayFilters from '@/components/filters/TimeOfDayFilters.vue'
import { isUpsell } from '@/helpers/Anchors'
import { Interval } from '@/helpers/ConflictHelpers'
import { dateQueryParamToAPIRange } from '@/helpers/Date'
import { mapEntries } from '@/helpers/DictHelpers'
import environment from '@/helpers/Environment'
import { eventListingTitle } from '@/helpers/LanguageHelpers'
import { eventHasMetadataValues, metadataKeys, removeQueryParams } from '@/helpers/QueryStringParameters'
import { isForMembersOnly } from '@/helpers/TicketGroupHelpers'
import { momentTo24hTime } from '@/helpers/TimeOfDay'
import { isLoggedIn } from '@/state/Authentication'
import { currentMembership } from '@/state/Membership'
import store from '@/store/store'
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { teaserImageSize } from '@/helpers/ImageUrls'

/**
 * Filterable list of events customer can select for their primary tickets.
 *
 * Filter state is the value prop. The parent component is responsible for updating it
 * either with v-model or in response to @input events.
 *
 * TODO Use <router-link> for each event so things like "open in new tab" work.
 * TODO Merge with <EventListingRoute>?
 */
@Component({
  name: 'EventListingWithFilters',
  components: { TimeOfDayFilters, CategoryFilters, EventListingItem, DateFilters, MembersBanner },
})
export default class extends Vue {
  @Prop({ required: true })
  value: Dictionary

  data: EventDetails[] | null = null

  $el: HTMLElement

  loading: boolean = false

  created() {
    this.fetch().then(() => {
      if (this.isUpsellListing && this.templates.length < 1) {
        // Skip upsells if there are none.
        this.$router.replace('/checkout')
      }
    })
  }

  mounted() {
    this.setTeaserImageSize('height')
    this.setTeaserImageSize('width')
  }

  private setTeaserImageSize(dimension: string): void {
    const value = teaserImageSize()[dimension]
    if (value) {
      this.$el.style.setProperty(`--teaser-image-${dimension}`, `${value}px`)
    }
  }

  get showMembersOnlyEvents() {
    // Show members only events when:
    // - User is logged in, whether they have an active membership or not
    // - There is an effective membership, from the user's logged-in state or the cart
    return isLoggedIn() || currentMembership()
  }

  get templates() {
    // TODO Disable/hide time filter start on timed entry events.
    if (!this.data) {
      return []
    }

    const cartHasAnchor = store.getters['Cart/anchor'] != null
    const events = this.data
      // Exclude anchored upsells unless the cart is anchored.
      .filter((event) => cartHasAnchor || !isUpsell(event))
      // Exclude member only events unless member benefits are available.
      .filter((event) => this.showMembersOnlyEvents || !isForMembersOnly(event))

    if (!this.timeFilters) {
      return events
    } else {
      // If there are time filters, then the _ondate option was
      // provided to getEvents(), and sessions are included.
      const eventsWithSessions = events as NestedEventDataWithSessions[]
      return eventsWithSessions.filter((event) =>
        event.sessions.some((session: Session) => {
          const { start, end } = mapEntries(this.timeFilters!, Number)
          const sessionStart = Number(momentTo24hTime(session.startMoment))
          // The end-time of the session does not matter.
          // TODO Consider supporting some padding, perhaps via metadata config.
          return start <= sessionStart && sessionStart < end
        }),
      )
    }
  }

  get timeFilters(): { start: string; end: string } | undefined {
    if (this.value.time || this.visitWindow) {
      return {
        // Use the visit window if no time query parameters are set yet.
        start: this.value['time.start'] || momentTo24hTime(this.visitWindow.start),
        end: this.value['time.end'] || momentTo24hTime(this.visitWindow.end),
      }
    } else {
      return undefined
    }
  }

  get visitWindow(): Interval {
    // Reserving an anchor event (adding to cart) sets this.
    return store.getters['Cart/visitWindow']
  }

  get showTimeFilters(): boolean {
    // Are time filters enabled in the tenant configuration?
    return (
      this.opt.timeFilters &&
      // Is there a visit window to filter by?
      this.visitWindow &&
      // Time filters are not useful on same_start upsells, because the start times must match exactly.
      environment.web.upsells?.visit_time !== 'same_start'
    )
  }

  get filteredTemplates() {
    const filters: Function[] = []

    if (this.value.category) {
      filters.push((template) => template.category === this.value.category)
    }

    if (this.filteredByMetadata) {
      filters.push((template) => eventHasMetadataValues(template, this.value))
    }

    return this.templates.filter((template) => filters.every((filter) => filter(template)))
  }

  get metadataKeys(): string[] {
    return metadataKeys(this.value)
  }

  get filteredByMetadata(): boolean {
    return this.metadataKeys.length > 0
  }

  get metadataFiltersLanguage() {
    return this.metadataKeys.map((key) => this.value[key]).join(', ')
  }

  get title() {
    return eventListingTitle()
  }

  get isUpsellListing() {
    return store.getters['Cart/cartHasItems']
  }

  @Watch('isUpsellListing')
  @Watch('value.date')
  fetch() {
    const options: Dictionary = {}
    this.loading = true

    const date = this.value.date
    if (date) {
      const [start, end] = dateQueryParamToAPIRange(date)

      if (start && end) {
        // This permits events which end after the filtered date,
        // but it is more performant than querying on multiple indexes.
        // Filters cover whole days and events usually do not cross midnight.
        options['event_session.start_datetime._gte'] = start
        options['event_session.start_datetime._lt'] = end
      }
    }

    if (this.isUpsellListing) {
      const visitWindow = store.getters['Cart/visitWindow']
      if (visitWindow) {
        // This triggers getEvents() to include all sessions, instead of just the first session.
        // @see getEvents()
        options._ondate = visitWindow.start.format('YYYY-MM-DD')
      }

      const categories = environment.web.upsells?.categories
      if (categories) {
        options['category._in'] = categories.join(',')
      }
    }

    if (!this.filteredByMetadata) {
      options['meta.metakey._in'] = 'image_profile,image_alt_text,no_cropping'
    }

    return fetchEventsAsNestedStructuredData(options)
      .then((results) => {
        this.data = results
      })
      .finally(() => (this.loading = false))
  }

  get clearMetaQueryParams() {
    return removeQueryParams(this.$route, this.metadataKeys)
  }

  get isUpsell(): boolean {
    return this.$store.getters['Cart/cartHasItems']
  }
}
